import { DatePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DivListComponent } from "@components/list/div-list/div-list.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import moment from "moment";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import {
    Anskaffningsformer,
    Arbetsordertyper,
    Avverkningsgrupper,
    ConfirmDialogResponse,
    Planeringsstatus,
    Produktionsstatus
} from "~app/common/models/constants";
import { VillkorligtDatumDTO } from "~app/common/models/planera/VillkorligtDatumDTO";
import { FormatService } from "~app/common/services/format.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ArbetsorderAtgardskedjaBehaviour } from "~app/features/planera/behaviours/ArbetsorderAtgardskedjaBehaviour";
import { AtgardAtgardskedjaBehaviour } from "~app/features/planera/behaviours/AtgardAtgardskedjaBehaviour";
import { SetGrotuttagCommand } from "~app/features/planera/commands/SetGrotuttagCommand";
import { TaBortGrotatgardCommand, TaBortGrotatgardDto } from "~app/features/planera/commands/TaBortGrotatgardCommand";
import { Barighet } from "~app/features/planera/model/Barighet";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { IAtgardBarighetChanged } from "~app/features/planera/model/IAtgardBarighetChanged";
import { IAtgardskedjaKoppling } from "~app/features/planera/model/IAtgardskedja";
import { ArbetsorderMediatorService } from "~app/features/planera/services/arbetsorder-mediator.service";
import { Produktionsstatusar } from "~app/features/planera/services/arbetsorder.service";
import { PlaneraBridgeEvents, PlaneraEventService } from "~app/features/planera/services/planera-event.service";
import { PlaneraService } from "~app/features/planera/services/planera.service";
import { environment } from "~environments/environment";
import { DecimalPipe } from "../../../../../common/pipes/decimal.pipe";
import { ArbetsorderWarningPillComponent } from "../../../arbetsorder-warning-pill/arbetsorder-warning-pill.component";
import { PlaneringsstatusIndicatorComponent } from "../../../planeringsstatus-indicator/planeringsstatus-indicator.component";
import { ReturnAtgardToGissPopupComponent } from "../return-atgard-to-giss-popup/return-atgard-to-giss-popup.component";

@Component({
    selector: "rask-atgard-info-summary",
    templateUrl: "./atgard-info-summary.component.html",
    styleUrls: ["./atgard-info-summary.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextmultilineComponent,
        ArbetsorderWarningPillComponent,
        RouterLink,
        PlaneringsstatusIndicatorComponent,
        SelectorComponent,
        FormsModule,
        CheckboxComponent,
        RadiobuttonGroupComponent,
        ButtonComponent,
        DivListComponent,
        ListColumnDirective,
        DatePipe,
        DecimalPipe
    ]
})
export class AtgardInfoSummaryComponent implements OnInit, OnChanges {
    readonly formatService = inject(FormatService);

    anskaffningsformer = Anskaffningsformer;
    @Input() arbetsorder: IArbetsorderDetail;
    @Input() atgard: IAtgard;
    @Input() setFormDirty: () => void;
    barigheter: string[] = Barighet.Alla;
    @Input() canSave: boolean;
    @Input() isEditable: boolean;
    relateradAtgard: IAtgardskedjaKoppling;
    relateradeArbetsorderAtgardKoppling: IAtgardskedjaKoppling[];
    @Input() updatedAtgardBarighet: IAtgardBarighetChanged[];

    private readonly arbetsorderMediator = inject(ArbetsorderMediatorService);
    private readonly busyService = inject(BusyService);
    private readonly planeraEventService = inject(PlaneraEventService);
    private readonly planeraService = inject(PlaneraService);
    private readonly raskModalService = inject(RaskModalService);

    canMakuleraAtgard(): boolean {
        const isGrotskotning = this.arbetsorder && this.arbetsorder.isGrotskotning;
        if (!isGrotskotning) {
            return false;
        }
        const isEgenSkog = this.arbetsorder.anskaffningsform === Anskaffningsformer.Egenskog;
        const isProduktionsplaneringPagar = this.arbetsorder.produktionsstatus === Produktionsstatus.ProduktionEjPaborjad;
        const isAtgardTillganglig = this.atgard && ObjectUtils.hasValue(this.atgard)
            && this.atgard.planeringsstatus === Planeringsstatus.Tillgangligt;

        return isEgenSkog && isProduktionsplaneringPagar && isAtgardTillganglig;
    }

    isSkordarmatning() {
        return this.arbetsorder.skordarmatning;
    }

    canReturnAtgardToGiss(): boolean {
        if (this.arbetsorder.internProduktionsstatus > Produktionsstatusar.produktionEjPaborjad) {
            return false;
        }

        return (this.canSave && this.isEditable
            && (this.arbetsorder
                && this.arbetsorder.anskaffningsform === Anskaffningsformer.Egenskog
                && this.arbetsorder.produktionsstatus === Produktionsstatus.ProduktionEjPaborjad)
            && (this.atgard && this.atgard.planeringsstatus === Planeringsstatus.Tillgangligt));
    }

    canSetGrotanpassning(): boolean {
        if (this.relateradeArbetsorderAtgardKoppling?.some(r => r.skickadTillViol)) {
            return false;
        }

        if (!this.atgard) {
            return false;
        }
        if (this.arbetsorder.anskaffningsform !== Anskaffningsformer.Egenskog) {
            return false;
        }

        const applicableAvverkningsgrupper = [
            Avverkningsgrupper.Foryngringsavverkning,
            Avverkningsgrupper.Gallring,
            Avverkningsgrupper.NSAvverkning
        ];

        if (this.atgard.grotuttagFaltplanerat || applicableAvverkningsgrupper.some(x => x === this.atgard.avverkningsgrupp)) {
            return true;
        }

        return false;
    }

    existInNavetApp() {
        const isGrotskotning = this.arbetsorder && this.arbetsorder.isGrotskotning;
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.arbetsorder.anskaffningsform === Anskaffningsformer.Egenskog
            && !isGrotskotning) {
            return true;
        }

        return false;
    }

    isArbetsorderTillganglig(): boolean {
        return this.arbetsorder.planeringsstatus === Planeringsstatus.Tillgangligt;
    }

    isBetweenDateRange(villkorligtdatum: VillkorligtDatumDTO): boolean {
        const slutdatum = moment(villkorligtdatum.slutdatum);
        const today = moment(new Date());

        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (moment(slutdatum).isBefore(today, "day")) {
            return false;
        }

        return true;
    }

    isRelateradAtgardGrot(): boolean {
        return this.relateradAtgard?.arbetsorderTyp === Arbetsordertyper.Grotskotning;
    }

    modifyGrotuttagProduktionsplanerat = (): void => {
        const atgard = this.atgard;
        const action = () => {
            this.arbetsorder.commandManager.addOrUpdate(new SetGrotuttagCommand(x => {
                x.atgardId = atgard.id;
                x.grotuttagProduktionsplanerat = atgard.grotuttagProduktionsplanerat;
            }), command => command.atgardId === atgard.id);
        };

        const needsConfirmation = atgard.grotuttagFaltplanerat !== true && atgard.grotuttagProduktionsplanerat;
        if (!needsConfirmation) {
            action();
            return;
        }

        this.raskModalService.confirm(new ModalSettings("Grotanpassa",
            `Du har valt att grotanpassa en åtgärd där grot ej är föreslagen i GISS.
            Observera att anmälan om grotuttag måste skickas till Skogsstyrelsen. Vill du gå vidare?`))
            .subscribe(
                {
                    next: result => {
                        if (result === ConfirmDialogResponse.save) {
                            action();
                        } else {
                            this.atgard.grotuttagProduktionsplanerat = false;
                        }

                    },
                    error: (_) => {
                        this.atgard.grotuttagProduktionsplanerat = false;
                    }
                }
            );
    };

    navetAppLink = (): string => `${environment.navetAppUrl}atgard/${this.atgard.beteckning}`;

    ngOnChanges(changes: SimpleChanges): void {
        const atgardAtgardskedja = new AtgardAtgardskedjaBehaviour(this.atgard);
        this.relateradAtgard = atgardAtgardskedja.getRelateradAtgardskoppling();

        const arbetsorderAtgardskedja = new ArbetsorderAtgardskedjaBehaviour(this.arbetsorder);
        this.relateradeArbetsorderAtgardKoppling = arbetsorderAtgardskedja.getRelateradAtgardskoppling();
    }

    ngOnInit(): void {
        if (this.atgard.grotuttagProduktionsplanerat === null) {
            this.atgard.grotuttagProduktionsplanerat = false;
        }
    }

    removeAtgard(): void {
        this.raskModalService.confirm(new ModalSettings("Ta bort åtgärd?", "Är du helt säker på att du vill ta bort åtgärden? Arbetsordern kommer att laddas om."))
            .subscribe(
                {
                    next: result => {
                        if (result === ConfirmDialogResponse.save) {
                            const command = new TaBortGrotatgardCommand((x) => {
                                x.payload = new TaBortGrotatgardDto(this.atgard.id, this.arbetsorder.id);
                            });
                            this.busyService.addWork(this.planeraService.handleCommand(command)).subscribe(() => this.arbetsorderMediator.reload());
                        }
                    },
                    error: (_) => {
                        //
                    }
                }
            );
    }

    returnAtgardToGissPopup() {
        this.raskModalService.openAsync("Kommentar returnering av åtgärd till GISS", ReturnAtgardToGissPopupComponent, "lg", false, "").subscribe({
            next: (action) => {
                if (action !== ConfirmDialogResponse.cancel) {
                    this.onReturnToGiss(action);
                }
            },
            error: (err) => {
                //
            }
        });
    }

    updateAtgardBarighet() {
        if (this.updatedAtgardBarighet.find(x => x.id === this.atgard.id)) {
            this.updatedAtgardBarighet.splice(this.updatedAtgardBarighet.findIndex(x => x.id === this.atgard.id), 1);
        }

        this.updatedAtgardBarighet.push({
            id: this.atgard.id,
            tillganglighetMark: this.atgard.tillganglighetMark,
            tillganglighetVag: this.atgard.tillganglighetVag
        });
        this.setFormDirty();
    }

    updateAtgardBarighetMark(barighet) {
        if (this.atgard.tillganglighetMark === barighet) {
            return;
        }

        this.atgard.tillganglighetMark = barighet;
        this.updateAtgardBarighet();
    }

    updateAtgardBarighetVag(barighet) {
        if (this.atgard.tillganglighetVag === barighet) {
            return;
        }

        this.atgard.tillganglighetVag = barighet;
        this.updateAtgardBarighet();
    }

    private onReturnToGiss(comment: string): void {
        const work$ = this.planeraService.returnToGiss(this.arbetsorder.id, this.atgard.guid, this.arbetsorder.rowVersion, comment);
        this.busyService.addWork(work$)
            .subscribe(result => {
                if (result && result.atgardWasReturned) {
                    if (!ObjectUtils.isNullOrUndefined(result.arbetsorderId)) {
                        this.arbetsorderMediator.reload();
                    } else {
                        // Om result.arbetsorder == null så har arbetsordern tagits bort p.g.a. avsaknad av åtgärder
                        // Skicka tillbaka till arbetsorderlistan
                        this.planeraEventService.emit(PlaneraBridgeEvents.arbetsorderProbablyDeleted, { arbetsorderId: this.arbetsorder.id });
                        this.arbetsorderMediator.setArbetsorder(null);
                    }
                } else if (result && !result.atgardWasReturned) {
                    const settings = new ModalSettings("FEL", "Negativt svar från Navet/Giss. Åtgärden gick ej att returnera!");
                    settings.okCaption = "Stäng";
                    this.raskModalService.info(settings).subscribe();
                }
            });
    }
}
